// FenerbahceVision2035.tsx

import React, {
  useState,
  useEffect,
  ChangeEvent,
  FormEvent,
} from 'react';
import {
  Mail,
  User,
  Trophy,
  Heart,
  Star,
  Shield,
  Target,
  Users,
  Award,
  ChevronDown,
  Play,
  Zap,
  Globe,
  TrendingUp,
  Sparkles,
  ArrowRight,
} from 'lucide-react';
import arena from './assets/arena.png';
import mac1 from './assets/mac1.png';
import mac2 from './assets/mac2.png';
import samandıra from './assets/samandıra.png';
import stad from './assets/stad.png';
import tesis from './assets/tesis.png';
import {
  motion,
  useViewportScroll,
  useTransform,
  MotionValue,
} from 'framer-motion';

import AnimatedBackground from './components/AnimatedBackground';
import Header from './components/Header';
import ParallaxImages from './components/ParallaxImages';
import VisionHero from './components/VisionHero';
import StatsGrid from './components/StatsGrid';
import VisionCardsGrid from './components/VisionCardsGrid';
import TabBar from './components/TabBar';
import MembershipForm from './components/MembershipForm';
import NewsletterForm from './components/NewsletterForm';
import InfoAccordion from './components/InfoAccordion';
import Footer from './components/Footer';
import {
  MousePosition,
  MembershipFormData,
  NewsletterFormData,
  AcademySection,
  Stat,
  VisionCard,
  TabItem,
} from './types';

// --- Main Page Container ---

const FenerbahceVision2035: React.FC = () => {
  const { scrollY } = useViewportScroll();
  const yPos = useTransform(scrollY, [0, 500], [0, -100]);

  const [activeTab, setActiveTab] = useState<string>('membership');
  const [mousePosition, setMousePosition] =
    useState<MousePosition>({ x: 0, y: 0 });
  const [isLoaded, setIsLoaded] = useState<boolean>(false);

  const [formData, setFormData] = useState<MembershipFormData>({
    name: '',
    surname: '',
    email: '',
    phone: '',
    birthDate: '',
    address: '',
    district: '', // Added district
    city: '', // Added city
    memberNo: '',
    university: '',
    department: '',
    graduationYear: '',
    certifications: '',
    expertise: [],
    applicationReason: '',
    suggestion: '',
    contributions: '',
    cvFile: null,
    projectLinks: '',
    personalSite: '',
  });

  const [newsletterForm, setNewsletterForm] =
    useState<NewsletterFormData>({
      fullname: '',
      email: '',
      interests: [],
    });

  const IMAGES = [
    { src: arena, alt: 'Ülker Stadyumu Arena' },
    { src: mac1, alt: 'Maç Anı 1' },
    { src: mac2, alt: 'Maç Anı 2' },
    { src: samandıra, alt: 'Samandıra Tesisleri' },
    { src: stad, alt: 'Stad Görünümü' },
    { src: tesis, alt: 'Tesisler' },
  ];

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) =>
      setMousePosition({ x: e.clientX, y: e.clientY });
    window.addEventListener('mousemove', handleMouseMove);
    setTimeout(() => setIsLoaded(true), 100);
    return () =>
      window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const [showNavbar, setShowNavbar] = useState<boolean>(true);
  const [lastY, setLastY] = useState<number>(0);
  useEffect(() => {
    const onScroll = () => {
      const currentY = window.scrollY;
      setShowNavbar(currentY < lastY || currentY < 50);
      setLastY(currentY);
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, [lastY]);

  // --- Handlers ---

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type, checked, files } = e.target as HTMLInputElement;
    if (type === 'checkbox' && name === 'expertise') {
      setFormData((prev) => ({
        ...prev,
        expertise: checked
          ? [...prev.expertise, value]
          : prev.expertise.filter((item) => item !== value),
      }));
    } else if (type === 'file') {
      setFormData((prev) => ({
        ...prev,
        [name]: files && files[0] ? files[0] : null,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    alert('Başvurunuz alındı! Fenerbahçe ailesine hoş geldiniz!');
  };

  const handleNewsletterInput = (
    e: ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setNewsletterForm((prev) => ({
        ...prev,
        interests: checked
          ? [...prev.interests, value]
          : prev.interests.filter((item) => item !== value),
      }));
    } else {
      setNewsletterForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleNewsletterSubmit = (
    e: FormEvent<HTMLFormElement>
  ) => {
    e.preventDefault();
    alert('Bülten aboneliğiniz başarıyla tamamlandı!');
  };

  const stats: Stat[] = [
    {
      icon: Trophy,
      number: '28',
      label: 'Şampiyonluk',
      color: 'from-yellow-400 to-yellow-600',
    },
    {
      icon: Users,
      number: '25M',
      label: 'Taraftar',
      color: 'from-blue-400 to-blue-600',
    },
    {
      icon: Award,
      number: '117',
      label: 'Yıl Tarih',
      color: 'from-green-400 to-green-600',
    },
    {
      icon: Target,
      number: '2035',
      label: 'Vizyon',
      color: 'from-purple-400 to-purple-600',
    },
  ];

  const visionCards: VisionCard[] = [
    {
      title: 'Dijital Dönüşüm',
      description: 'Teknoloji ile geleceğin spor deneyimini yaratıyoruz',
      icon: Zap,
      gradient: 'from-blue-500 via-purple-500 to-pink-500',
      delay: 0,
    },
    {
      title: 'Sürdürülebilirlik',
      description: 'Çevre dostu ve sürdürülebilir spor kompleksleri',
      icon: Globe,
      gradient: 'from-green-400 via-emerald-500 to-teal-500',
      delay: 200,
    },
    {
      title: 'Gençlik Gelişimi',
      description: 'Gelecek nesil sporcuları yetiştiriyoruz',
      icon: TrendingUp,
      gradient: 'from-orange-400 via-red-500 to-pink-500',
      delay: 400,
    },
    {
      title: 'Global Vizyon',
      description: 'Dünya çapında Fenerbahçe markasını büyütüyoruz',
      icon: Sparkles,
      gradient: 'from-purple-400 via-indigo-500 to-blue-500',
      delay: 600,
    },
  ];

  const academySections: AcademySection[] = [
    {
      title: '1. Kuruluş Esasları',
      items: [
        `Fenerbahçe Vizyon 2035 oluşumu; Fenerbahçe Spor Kulübü’nün 10 yıllık geleceğine yönelik stratejik planlamalar yapan, bilimsel araştırma ve çalışmalar doğrultusunda çağın gereklerine uygun, veriye dayalı fikirler üreten, üst akıl niteliğinde bir düşünce ve inovasyon platformudur.`,
        `Bu platform; Fenerbahçe Spor Kulübü’nün sosyal, sportif, dijital, kültürel ve ekonomik geleceğini şekillendirmek amacıyla oluşturulan, disiplinlerarası uzmanlardan oluşan “stratejik bir düşünce ve araştırma grubu”dur.`,
      ],
    },
    {
      title: '2. Kuruluş Amaçları',
      items: [
        `Fenerbahçe Vizyon 2035 oluşumunun kuruluş amaçları; Fenerbahçeli kongre üyelerinin özgürce fikirlerini beyan ettiği, tartıştığı, herhangi bir spor dalı ayırt etmeksizin Fenerbahçe’nin sorunlarına çözüm aradığı tartışma platformu oluşturmak ve bu vesileyle bulduğu çözümleri kulübe ve kamuoyuna bildirmektir.`,
        'Fenerbahçe’nin yarınlarında bu platformdan yöneticiler ve başkanlar çıkarmak.',
        'Fenerbahçe demokrasisini kesintisiz biçimde işletmek.',
        'Fenerbahçe’nin yarınlarında kongre üyelerinin daha çok söz sahibi olmasını sağlamak.',
        'Yeni projeler üretmek ve ürettiği projeleri Fenerbahçe’nin geleceği için hayata geçirmek amacıyla kulübe sunmak.',
        'Yapacağı toplantılar ve oturumlarla Fenerbahçeli kongre üyelerini bilinçlendirmek, her konuda bilgi sahibi yapmak.',
        'Fenerbahçe ile ilgili toplantılarda (Yüksek Divan Kurulu, Olağan veya olağanüstü kongre, mali kongre) söz alıp mevcut iktidarla fikir ve görüşlerini paylaşmak.',
      ],
    },
    {
      title: '3. Çalışma Prensipleri',
      items: [
        'Bilimsel yöntemle araştırma yapma',
        'Tarafsız ve kulüp menfaatine odaklı yaklaşım',
        'Veriye dayalı karar önerileri',
        'Şeffaflık ve paylaşım kültürü',
      ],
    },
    {
      title: '4. 5 Yıllık Faaliyet Planı',
      items: [
        '6 ayda bir “Vizyon 2035 Faaliyet Raporu”',
        '6 ayda bir “Vizyon 2035 Buluşması”',
        '12 ayda bir kulübün mevcut yönetim kuruluna “Vizyon 2035 Rapor Sunumları”',
        '18 ayda bir “Vizyon 2035 Zirvesi”',
      ],
    },
    {
      title: '5. Organizasyon Yapısı',
      items: [
        `İcra Kurulu
  Amaç: Stratejik kararları uygulamaya koyan, operasyonel süreçleri yöneten yürütücü organdır.
  Görev Tanımları:
  - Komisyonlardan ve koordinasyon kurulundan gelen karar ve projeleri hayata geçirmek.
  - Belirlenen hedeflere yönelik aksiyon planlarını oluşturmak ve uygulamak.
  - Süreçlerin takibini yapmak, raporlamak.
  - Gerekli bütçe ve kaynak yönetimini sağlamak.
  - Yönetim Kurulu’na (veya bu yapının üst organına) düzenli raporlama yapmak.
  - Kriz anlarında hızlı karar alma mekanizması olarak devreye girmek.`,
        `Koordinasyon Kurulu
  Amaç: Farklı komisyonlar ve kurullar arasında işbirliğini, bilgi akışını ve uyumu sağlayan merkezî yapıdır.
  Görev Tanımları:
  - Tüm komisyonların ve danışma kurulunun çalışmalarını eşgüdüm içinde yürütmek.
  - Komisyonların oluşumunu, gelişimini, yeni üyelerin görevlendirilmesini, üzerinde çalışılacak konu başlıklarını takip ve koordine etmek.
  - Faaliyet takvimlerini koordine etmek, çakışmaları önlemek.
  - Bilgi, belge ve çıktıların paylaşımını standartlaştırmak.
  - İcra Kurulu ile komisyonlar/danışma kurulu arasında köprü görevi görmek.
  - Gerekli durumlarda süreç iyileştirmeleri için öneriler sunmak.`,
        `Danışma Kurulu
  Amaç: Deneyim, birikim ve vizyon sahibi kişilerden oluşan; görüş, öneri ve rehberlik sağlayan kuruldur. (Kulüp içinden ve akademik çevrelerden mentorlar, kulüp eski yöneticileri, akademisyenler, gazeteciler)
  Görev Tanımları:
  - FB Vizyon 2035 oluşumun genel stratejisine dair yol gösterici fikirler üretmek.
  - Komisyonların çalışmalarını değerlendirmek, geri bildirimde bulunmak.
  - İcra ve Koordinasyon Kurulları tarafından danışılan konularda görüş beyan etmek.
  - Gerektiğinde kamuya açık bildiriler ve görüş yazıları hazırlamak.
  - Kurum hafızasını ve sürekliliği temsil etmek.`,
        `Komisyonlar
  Amaç: Ulusal ve uluslararası örnekleri inceleyerek Fenerbahçe’ye özgü modeller üreterek; uzmanlık alanlarında veri toplamakla, analiz yapmakla, projeler geliştirmekle ve fizibilite raporları hazırlamakla ve tüm bunların ışığında kulüp menfaatlerine yönelik öneri geliştirmek ve proje üretmekle yükümlü kurullardır.
  Görev Tanımları:
  - Fenerbahçe Vizyon 2035 oluşumunun genel stratejisine dair yol gösterici fikirler üretmek.
  - Komisyonların çalışmalarını değerlendirmek, geri bildirimde bulunmak.
  - İcra ve Koordinasyon Kurulları tarafından danışılan konularda görüş beyan etmek.
  - Gerektiğinde kamuya açık bildiriler ve görüş yazıları hazırlamak.
  - Kurum hafızasını ve sürekliliği temsil etmek.
  - Komisyonlara mentorluk desteği sağlamak, stratejik yönlendirmeler sunmak.
  - Ulusal ve uluslararası gelişmeler ışığında kulübün uzun vadeli hedeflerine katkı verecek perspektifler geliştirmek.
  - Vizyon 2035 kapsamında toplumsal etkiyi ve iletişimi güçlendirmeye yönelik öneriler geliştirmek.`,
        `1. Eğitim, Bilim ve Spor Akademisi Komisyonu
  Amaç: Kulübün sporcu, antrenör ve yönetici eğitim altyapısını kurmak, bilimsel çalışmaları teşvik etmek ve sürdürülebilir bir sporcu yetiştirme modeli geliştirmek.
  Görevler: Akademi modelinin kurulması, antrenman metodolojileri geliştirme, eğitim içerikleri ve müfredat oluşturma.
  Faaliyetler: Sporcu kampları, seminerler, mentorluk programları, bilimsel araştırmalar, ulusal ve uluslararası eğitim iş birlikleri.
  Üye Profili: Akademisyenler, antrenörler, spor psikologları, eğitimciler, eski sporcular.
  Çıktılar: Kulüp akademi modeli, antrenör geliştirme programı, eğitim kitapçıkları, performans analiz sistemleri.`,
        `2. Tarih, Kültür ve Kulüp Hafızası Komisyonu
  Amaç: Fenerbahçe’nin kültürel mirasını belgelemek, yaşatmak ve gelecek kuşaklara aktarmak.
  Görevler: Arşiv çalışmaları, belgesel projeleri, müze tasarımı ve dijital hafıza projeleri.
  Faaliyetler: Sözlü tarih çalışmaları, müze etkinlikleri, anma törenleri, yayınlar.
  Üye Profili: Tarihçiler, kulüp emektarları, arşiv uzmanları, medya temsilcileri.
  Çıktılar: Kulüp dijital arşivi, belgesel serisi, tarih kitapları, müze koleksiyonları.`,
        `3. Küresel Futbol Gelişim ve Altyapı AR-GE Komisyonu
  Amaç: Futbolun bilimsel ve teknolojik gelişimini izlemek, altyapıdan A takıma kadar entegrasyonu sağlamak.
  Görevler: Yetenek keşfi, veri temelli performans sistemleri, global kulüp iş birlikleri.
  Faaliyetler: Altyapı scout çalışmaları, AR-GE raporları, teknolojik uygulama testleri.
  Üye Profili: Futbol bilim insanları, analistler, altyapı antrenörleri, scout ekipleri.
  Çıktılar: Altyapı gelişim modeli, performans veritabanı, transfer ve scout raporları.`,
        `4. Dernekler, Taraftar ve Toplumla İletişim Komisyonu
  Amaç: Tüm Fenerbahçe camiasıyla etkili ve sürekli bir iletişim köprüsü kurmak.
  Görevler: Dernek koordinasyonu, taraftar ilişkileri, toplum temelli projeler.
  Faaliyetler: Taraftar çalıştayları, dernek buluşmaları, iletişim kampanyaları.
  Üye Profili: İletişim uzmanları, dernek temsilcileri, sosyal medya yöneticileri.
  Çıktılar: Taraftar geri bildirim raporları, dernek ağ haritası, iletişim platformları.`,
        `5. Stadyum Yönetimi, Organizasyonlar ve Etkinlikler Komisyonu
  Amaç: Stadımızın fiziksel ve lojistik yönetimini geliştirerek etkinlikleri arttırmak.
  Görevler: Etkinlik planlaması, güvenlik ve konfor iyileştirmeleri, gelir artırıcı projeler.
  Faaliyetler: Konserler, turnuvalar, organizasyon kılavuzları.
  Üye Profili: Etkinlik yöneticileri, mimar-mühendisler, emniyet uzmanları.
  Çıktılar: Stadyum gelir modeli, etkinlik takvimi, deneyim artırıcı uygulamalar.`,
        `6. Sosyal Etki ve Sosyal Projeler Komisyonu
  Amaç: Kulübün sosyal sorumluluk gücünü artırmak, toplumsal farkındalık yaratmak.
  Görevler: Eğitim, sağlık, çevre ve eşitlik odaklı projelerin geliştirilmesi.
  Faaliyetler: Kız çocuklarının eğitimi, engellilik farkındalığı, çevre projeleri.
  Üye Profili: Sosyal girişimciler, STK temsilcileri, gönüllü ağları.
  Çıktılar: Sosyal etki raporları, proje ortaklıkları, sosyal kampanyalar.`,
        `7. Tesisleşme ve Yatırımlar Komisyonu
  Amaç: Kulübün tüm tesis altyapısının planlı gelişimini sağlamak.
  Görevler: Mevcut tesislerin yenilenmesi, yeni projelerin planlanması ve yatırım ortaklıkları.
  Faaliyetler: Kampüs projeleri, yeşilalan yatırımları, sürdürülebilir mimari çalışmaları.
  Üye Profili: Yatırım uzmanları, mimarlar, şehir plancıları, müteahhitler.
  Çıktılar: Tesis master planı, yatırım takvimi, ortaklık protokolleri.`,
        `8. Mali ve Finansal İşler Komisyonu
  Amaç: Kulübün mali disiplinini sağlamak ve finansal sürdürülebilirliğini yapılandırmak.
  Görevler: Gelir-gider dengesi, denetim mekanizmaları, yeni finans kaynakları üretimi.
  Faaliyetler: Şeffaflık raporları, denetim modelleri, finansal yapılandırma çalışmaları.
  Üye Profili: Mali müşavirler, finans danışmanları, ekonomistler.
  Çıktılar: Yıllık mali denge raporu, bütçe yönetim kılavuzu, sponsorluk finans raporları.`,
        `9. Kurumsallaşma ve İnsan Kaynakları Komisyonu
  Amaç: Kulübün tüm birimlerinde profesyonel yapı ve liyakat sistemini kurmak.
  Görevler: Organizasyon şeması oluşturma, iç denetim ve performans değerlendirme sistemleri kurma.
  Faaliyetler: İnsan kaynakları politikaları, yönetici eğitimleri, kariyer planlama çalışmaları.
  Üye Profili: İK uzmanları, yönetim danışmanları, liderlik koçları.
  Çıktılar: Kurumsallaşma rehberi, liyakat sistemi, eğitim müfredatları.`,
        `10. Mağazacılık, Pazarlama, Projeler ve Sponsorluklar Komisyonu
  Amaç: Kulübün marka değerini yükseltmek, ticari gelirleri artırmak.
  Görevler: Yeni mağaza ve ürün tasarımı, sponsor ilişkileri, pazarlama kampanyaları.
  Faaliyetler: Lansmanlar, dijital satış projeleri, lisanslı ürün geliştirme.
  Üye Profili: Pazarlama yöneticileri, kreatif ajans temsilcileri, girişimciler.
  Çıktılar: Marka strateji raporu, ürün kataloğu, sponsorluk sözleşmeleri.`,
        `11. Bilişim Teknolojileri ve Bilgi Sistemleri Yönetimi Komisyonu
  Amaç: Kulübün dijital dönüşümünü gerçekleştirmek ve veri güvenliğini sağlamak.
  Görevler: Taraftar verisi yönetimi, dijital projeler geliştirme, siber güvenlik protokolleri.
  Faaliyetler: Mobil uygulama, CRM sistemleri, yapay zekâ destekli analiz platformları.
  Üye Profili: Yazılım geliştiriciler, BT yöneticileri, veri analistleri.
  Çıktılar: Dijital ekosistem haritası, veri güvenliği kılavuzu, taraftar etkileşim sistemleri.`,
        `12. Hukuk Komisyonu
  Amaç: Kulübün tüm hukuki süreçlerini güvence altına almak ve hukuki şeffaflığı sağlamak.
  Görevler: Tüzük çalışmaları, sözleşme denetimi, dava süreçleri takibi.
  Faaliyetler: Hukuk danışmanlığı, mevzuat güncellemeleri, spor hukuku panelleri.
  Üye Profili: Avukatlar, hukuk danışmanları, spor hukuk akademisyenleri.
  Çıktılar: Güncel tüzük metni, hukuk kılavuzları, örnek sözleşmeler.`,
      ],
    },
    {
      title: '6. Üyelik Koşulları',
      items: [
        'Fenerbahçe Spor Kulübü Kongre Üyesi olmak.',
        'Fenerbahçe Spor Kulübü’nün temel değerlerine ve tarihine hakim olmak, vizyon ve misyonunu benimsemek.',
        'Üniversite mezunu veya yüksek lisans/doktora öğrencisi olmak.',
        'Etik değerlere ve şeffaflığa bağlılık gösterebilmek, kişisel ve mesleki geçmişinde bu değerlere aykırı bir durumun bulunmaması.',
        'Yenilikçi düşünme becerisi ve çözüm odaklı yaklaşım sergileyebilmek.',
        'Belirli aralıklarla yapılacak eğitim, atölye, toplantı ve organizasyonlara düzenli katılım taahhüdü vermek.',
        'Liderlik potansiyeline sahip olmak, daha önce gençlik yapılanmalarında, öğrenci kulüplerinde, sivil toplum kuruluşlarında veya projelerde aktif roller üstlenmiş olmak.',
        'Dijital okuryazarlık ve medya farkındalığına sahip olmak, sosyal medya, iletişim stratejileri ve dijital platformlara hâkim olmak.',
        'Tercihen üyelerin mensubu olduğu disiplinler: Spor yönetimi, ekonomi, iletişim, mühendislik, yapay zeka, sosyoloji, eğitim, yönetim, psikoloji ve hukuktur.',
        'Aşağıdaki üyelik formunu dolduran adayların üyelik başvurusu 15 günde bir toplanan icra kurulu tarafından onaylanır.',
      ],
    },
  ];



  const tabItems: TabItem[] = [
    { key: 'membership', label: 'Üyelik', icon: User },
    { key: 'info', label: 'Bilgi', icon: Play },
  ];

  return (
    <div className="min-h-screen h-auto bg-slate-900 relative overflow-x-hidden overflow-y-auto text-white w-full max-w-full px-0">
      <AnimatedBackground mousePosition={mousePosition} />
      <div
        className="fixed w-60 h-60 sm:w-80 sm:h-80 pointer-events-none z-10 rounded-full transition-all duration-1000"
        style={{
          left: mousePosition.x - 96,
          top: mousePosition.y - 96,
          background:
            'radial-gradient(circle, rgba(251,191,36,0.1) 0%, transparent 70%)',
        }}
      />
      <Header showNavbar={showNavbar} isLoaded={isLoaded} />
      <ParallaxImages yPos={yPos} IMAGES={IMAGES} />

      <div className="relative py-10 sm:py-16 px-0  z-10 w-11/12 mx-auto max-w-full ">
        <VisionHero isLoaded={isLoaded} />
        <TabBar
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          tabItems={tabItems}
        />

        <main className="w-full mx-auto lg:px-6 relative z-10 h-full max-w-full">
          <MembershipForm
            visible={activeTab === 'membership'}
            formData={formData}
            handleInputChange={handleInputChange}
            handleSubmit={handleSubmit}
          />
          <InfoAccordion
            visible={activeTab === 'info'}
            academySections={academySections}
          />
        </main>
        <StatsGrid stats={stats} isLoaded={isLoaded} />
        <VisionCardsGrid visionCards={visionCards} isLoaded={isLoaded} />
      </div>


      <Footer />
    </div>
  );
};

export default FenerbahceVision2035;
